/**
 * Modul "Das Ende": ausschließlich für den Systemadministrator sichtbar
 * (Route in App.tsx über ProtectedRoute requireSuperAdmin abgesichert,
 * Endpunkt serverseitig über requireSuperAdmin() in routes/systemAdmin.ts).
 * Löscht nach zweifacher Bestätigung sämtliche Daten unwiderruflich.
 */
import { useState, type FormEvent } from "react";
import { useAuth } from "../../context/AuthContext";
import { api, ApiError } from "../../api/client";
import { Card } from "../../components/ui/Card";

const CONFIRMATION_WORD = "LÖSCHEN";

export default function DasEnde() {
  const { logout } = useAuth();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const confirmationValid = confirmation === CONFIRMATION_WORD;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!confirmationValid) {
      setError(`Bitte exakt "${CONFIRMATION_WORD}" eingeben, um fortzufahren.`);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/api/system-admin/das-ende", { password, confirmation });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Aktion fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="max-w-xl mx-auto mt-12 text-center space-y-4">
        <div className="text-5xl">💀</div>
        <h1 className="text-xl font-display font-bold text-danger uppercase tracking-wide">Das Ende ist eingetreten</h1>
        <p className="text-sm text-gray-400">
          Sämtliche Daten wurden vollständig und unwiderruflich gelöscht - inklusive aller Benutzerkonten, Rollen und Berechtigungen. Ein Login ist bis zur manuellen Wiederherstellung der Datenbank (erneutes Einspielen der Migrationen) nicht mehr möglich.
        </p>
        <button className="btn-secondary" onClick={logout}>Zur Anmeldeseite</button>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto space-y-4">
      <Card className="p-6 border-2 border-danger bg-danger-muted/40 space-y-4">
        <div className="flex items-center gap-3">
          <span className="text-4xl">⚠️</span>
          <div>
            <h1 className="text-lg font-display font-bold text-danger uppercase tracking-wide">Das Ende</h1>
            <p className="text-xs text-danger/80 uppercase tracking-widest">Kritische Systemfunktion</p>
          </div>
        </div>

        <div className="bg-danger-muted border border-danger/50 rounded-lg px-4 py-3 text-sm text-gray-200 space-y-2">
          <p className="font-semibold text-danger">
            Diese Aktion löscht UNWIDERRUFLICH sämtliche Daten des gesamten Systems:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-0.5">
            <li>Alle Mitglieder, Familieninformationen, Notizen und Zugangsanfragen</li>
            <li>Die gesamte Kasse (Konten und Transaktionen) sowie das Lager</li>
            <li>Alle Benutzerkonten - inklusive dieses Systemadministrator-Kontos</li>
            <li>Sämtliche Rollen, Berechtigungen und das komplette Audit-Log</li>
          </ul>
          <p className="text-gray-300">
            Nach der Ausführung ist <span className="font-semibold text-danger">kein Login mehr möglich</span> - auch nicht für den Systemadministrator selbst. Eine Wiederherstellung erfordert manuellen Datenbankzugriff (erneutes Einspielen der Datenbank-Migrationen durch eine Entwicklerin/einen Entwickler).
          </p>
        </div>

        {error && <div className="bg-danger-muted border border-danger/50 text-danger text-sm rounded-lg px-3 py-2">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Ihr Passwort zur Bestätigung</label>
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <label className="label">
              Zur Bestätigung exakt "{CONFIRMATION_WORD}" eingeben
            </label>
            <input
              className="input"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder={CONFIRMATION_WORD}
              required
            />
          </div>
          <button
            type="submit"
            disabled={submitting || !password || !confirmationValid}
            className="btn-danger w-full py-2.5"
          >
            {submitting ? "Wird ausgeführt…" : "Jetzt unwiderruflich löschen"}
          </button>
        </form>
      </Card>
    </div>
  );
}
